import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts'; // This provides default Roboto fonts as fallback

// Import only the Poppins Regular and Bold base64 font data
// You MUST ensure PoppinsBoldBase64 is the base64 of Poppins-Bold.ttf (700 weight)
import {
  PoppinsSemiBoldBase64,
  PoppinsBoldBase64,
  PoppinsRegularBase64,
} from './font'; // Adjust this path as necessary
import { logoBase64 } from './data';
import { getRuntimeTenantConfig } from 'tenants/tenantConfigService';

const { branding: runtimeBranding } = getRuntimeTenantConfig() || {};

// --- Setup PDFMake VFS with Poppins ---
// eslint-disable-next-line
// @ts-ignore
pdfMake.vfs = pdfFonts || {}; // Standard way to assign default Roboto fonts

// Add your custom Poppins fonts to the VFS
// Ensure these filenames match what you define in pdfMake.fonts below.
pdfMake.vfs['Poppins-Regular.ttf'] = PoppinsRegularBase64;
pdfMake.vfs['Poppins-Bold.ttf'] = PoppinsBoldBase64;
pdfMake.vfs['PoppinsSemiBold-Bold.ttf'] = PoppinsSemiBoldBase64;

// Define the fonts for PDFMake.
pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-Italic.ttf',
  },
  Poppins: {
    normal: 'Poppins-Regular.ttf', // for font-weight 400
    bold: 'Poppins-Bold.ttf', // for font-weight 700 (and general 'bold' property)
  },
  PoppinsSemiBold: {
    normal: 'Poppins-Regular.ttf',
    bold: 'PoppinsSemiBold-Bold.ttf',
  },
};

interface Service {
  amount: string;
  name: string;
  description: string;
  currency_symbol: string;
}

interface CompanyInfo {
  ID: string;
  CompanyName: string;
  Location: string;
  Street: string;
  Phone: string;
  Email: string;
  GovID: string;
}

interface TimeLog {
  user_name: string;
  rate_per_hour: number;
  rate_per_hour_str: string;
  rate_per_day: number;
  rate_per_day_str: string;
  total_hours: number;
  total_days: number;
  total_fee: string;
}

export interface PDFInvoiceData {
  company_name: string;
  due_date: string;
  transaction_date: string;
  amount: string;
  discount_amount: string;
  total_amount: string;
  total_tax_amount: string;
  balance: string;
  billing_fullname: string;
  billing_street_1: string;
  location: string;
  VAT: string;
  term_value: string;
  term_name: string;
  transaction_fee: string;
  currency: string;
  currency_symbol: string;
  services: Service[];
  paid_total: string;
  company_info: CompanyInfo;
  year: number;
  time_logs: TimeLog[];
  adjustment_time_logs: TimeLog[];
  adjustment_month: string;
  adjustment_year: string;
  month: string;
  qb_region: string;
  invoice_number?: string;
}

const tableLayoutConfig = {
  vLineWidth: () => 2,
  vLineColor: () => '#FFFFFF',

  hLineWidth: (i: number) => (i === 0 || i === 1 ? 0 : 1),
  hLineColor: () => '#F15450', // Red horizontal line color

  // Padding inside cells
  paddingLeft: () => 4,
  paddingRight: () => 4,

  paddingTop: (i: number) => (i === 0 ? 1 : 8),
  paddingBottom: (i: number) => (i === 0 ? 0 : 4),

  // Fill color for header background
  fillColor: (rowIndex: number) => (rowIndex === 0 ? '#F15450' : null),
};

export const generateInvoicePDFMake = async (
  invoiceData: PDFInvoiceData
): Promise<string> => {
  const getBankAccountDetails = () => {
    if (invoiceData.qb_region === 'USA' && invoiceData.currency === 'USD') {
      return [
        [
          { text: 'Account currency:', style: 'accountDetailsLabel' },
          { text: invoiceData.currency, style: 'accountDetailsValue' },
        ],
        [
          { text: 'Bank name:', style: 'accountDetailsLabel' },
          { text: 'Bank of America', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Bank address:', style: 'accountDetailsLabel' },
          {
            text: '3250 Mission Street, San Francisco, CA 94110',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'Wire Desk help:', style: 'accountDetailsLabel' },
          { text: '1 (800) 831-4419', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Type of Account:', style: 'accountDetailsLabel' },
          { text: 'USD Checking', style: 'accountDetailsValue' },
        ],
        [
          { text: 'SWIFT Code:', style: 'accountDetailsLabel' },
          { text: 'BOFAUS3N', style: 'accountDetailsValue' },
        ],
        [
          { text: 'CHIPS:', style: 'accountDetailsLabel' },
          { text: '0959', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Beneficiary Bank:', style: 'accountDetailsLabel' },
          { text: 'Bank of America', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Beneficiary Bank ABA:', style: 'accountDetailsLabel' },
          {
            text: '121000358 (International Wires)',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'Routing:', style: 'accountDetailsLabel' },
          { text: '121000358 (Domestic Wires)', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Wires:', style: 'accountDetailsLabel' },
          { text: '026009593 (Domestic Wires)', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Beneficiary Bank Location:', style: 'accountDetailsLabel' },
          {
            text: 'Bank of America, 3250 Mission Street, San Francisco, CA 94110',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'Beneficiary Account #:', style: 'accountDetailsLabel' },
          { text: '325044292397', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Beneficiary Account Name:', style: 'accountDetailsLabel' },
          { text: 'Zazmic Inc.', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Beneficiary Address:', style: 'accountDetailsLabel' },
          {
            text: '79 Coleridge St. San Francisco, CA 94110',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'Type of Account:', style: 'accountDetailsLabel' },
          { text: 'USD Checking', style: 'accountDetailsValue' },
        ],
        [
          { text: 'SWIFT Code:', style: 'accountDetailsLabel' },
          { text: 'BOFAUS3N', style: 'accountDetailsValue' },
        ],
        [
          { text: 'CHIPS:', style: 'accountDetailsLabel' },
          { text: '0959', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Special Instructions:', style: 'accountDetailsLabel' },
          {
            text: 'Wire transfers must include the sender’s and company name.',
            style: 'accountDetailsValue',
          },
        ],
      ];
    }
    if (invoiceData.qb_region === 'USA' && invoiceData.currency === 'CAD') {
      return [
        [
          { text: 'Account currency:', style: 'accountDetailsLabel' },
          { text: invoiceData.currency, style: 'accountDetailsValue' },
        ],
        [
          { text: 'Recipient :', style: 'accountDetailsLabel' },
          { text: 'ZAZMIC INC', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Account number:', style: 'accountDetailsLabel' },
          { text: '200110990710', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Institution number:', style: 'accountDetailsLabel' },
          {
            text: '621 (Use for domestic transfers within Canada)',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'Transit number:', style: 'accountDetailsLabel' },
          {
            text: '16001 (Use for domestic transfers within Canada)',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'Swift/BIC:', style: 'accountDetailsLabel' },
          {
            text: 'TRWICAW1XXX (Use for transfers from outside Canada)',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'Bank name:', style: 'accountDetailsLabel' },
          { text: 'Wise Payments Canada Inc.', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Bank address:', style: 'accountDetailsLabel' },
          {
            text: '99 Bank Street, Suite 1420, Ottawa, ON, K1P 1H4, Canada',
            style: 'accountDetailsValue',
          },
        ],
      ];
    }
    if (invoiceData.qb_region === 'Europe' && invoiceData.currency === 'EUR') {
      return [
        [
          { text: 'Account currency:', style: 'accountDetailsLabel' },
          { text: invoiceData.currency, style: 'accountDetailsValue' },
        ],
        [
          { text: 'Recipient:', style: 'accountDetailsLabel' },
          { text: 'ZAZMIC INC LIMITED', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Recipient address:', style: 'accountDetailsLabel' },
          {
            text: 'GROUND FLOOR, 71 LOWER BAGGOT STREET, D02 P593, Dublin, Ireland',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'IBAN:', style: 'accountDetailsLabel' },
          { text: 'IE06 REVO 9903 6036 2037 44', style: 'accountDetailsValue' },
        ],
        [
          { text: 'BIC:', style: 'accountDetailsLabel' },
          { text: 'REVOIE23', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Intermediary BIC:', style: 'accountDetailsLabel' },
          { text: 'CHASDEFX', style: 'accountDetailsValue' },
        ],
      ];
    }
    if (invoiceData.qb_region === 'Europe' && invoiceData.currency === 'GBP') {
      return [
        [
          { text: 'Account currency:', style: 'accountDetailsLabel' },
          { text: invoiceData.currency, style: 'accountDetailsValue' },
        ],
        [
          { text: 'Recipient:', style: 'accountDetailsLabel' },
          { text: 'ZAZMIC INC LIMITED', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Recipient address:', style: 'accountDetailsLabel' },
          {
            text: 'GROUND FLOOR, 71 LOWER BAGGOT STREET, D02 P593, Dublin, Ireland',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'Account number:', style: 'accountDetailsLabel' },
          { text: '46047832', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Sort code:', style: 'accountDetailsLabel' },
          { text: '04-29-09', style: 'accountDetailsValue' },
        ],
        [
          { text: 'IBAN:', style: 'accountDetailsLabel' },
          { text: 'IE06 REVO 9903 6036 2037 44', style: 'accountDetailsValue' },
        ],
        [
          { text: 'BIC:', style: 'accountDetailsLabel' },
          { text: 'REVOIE23', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Intermediary BIC:', style: 'accountDetailsLabel' },
          { text: 'CHASGB2L', style: 'accountDetailsValue' },
        ],
      ];
    }
    if (invoiceData.qb_region === 'Europe' && invoiceData.currency === 'USD') {
      return [
        [
          { text: 'Account currency:', style: 'accountDetailsLabel' },
          { text: invoiceData.currency, style: 'accountDetailsValue' },
        ],
        [
          { text: 'Recipient:', style: 'accountDetailsLabel' },
          { text: 'ZAZMIC INC LIMITED', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Recipient address:', style: 'accountDetailsLabel' },
          {
            text: 'GROUND FLOOR, 71 LOWER BAGGOT STREET, D02 P593, Dublin, Ireland',
            style: 'accountDetailsValue',
          },
        ],
        [
          { text: 'IBAN:', style: 'accountDetailsLabel' },
          { text: 'IE06 REVO 9903 6036 2037 44', style: 'accountDetailsValue' },
        ],
        [
          { text: 'BIC:', style: 'accountDetailsLabel' },
          { text: 'REVOIE23', style: 'accountDetailsValue' },
        ],
        [
          { text: 'Intermediary BIC:', style: 'accountDetailsLabel' },
          { text: 'CHASGB2L', style: 'accountDetailsValue' },
        ],
      ];
    }

    // Fallback for any unhandled combinations, or an empty set of details
    return [
      [
        {
          text: 'No bank details available for this region and currency combination.',
          colSpan: 2,
          alignment: 'center',
          margin: [0, 10, 0, 10],
        },
        {},
      ],
    ];
  };

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [50, 16, 50, 45], // Left, Top, Right, Bottom

    defaultStyle: {
      font: 'Poppins', // Use Poppins here
      fontSize: 10,
      lineHeight: 1.33,
      color: '#000000',
    },

    styles: {
      // Font sizes (fX classes)
      f8: { fontSize: 9, lineHeight: 1 },
      f9: { fontSize: 8 },
      f10: { fontSize: 10 },
      f12: { fontSize: 12, lineHeight: 1.4 },
      f14: { fontSize: 14, lineHeight: 1.42 },
      f16: { fontSize: 16, lineHeight: 1.5 },
      f18: { fontSize: 18, lineHeight: 1.5 },
      f20: { fontSize: 20, lineHeight: 1.5 },
      f24: { fontSize: 24, lineHeight: 1.5 },

      // Combined header main title style
      headerMainTitle: {
        fontSize: 28,
        bold: true,
        color: '#F15450',
        characterSpacing: 3,
        lineHeight: 1,
      },
      charSpacing1: { characterSpacing: 0 },
      charSpacing2: { characterSpacing: 2 },

      // Colors
      red: { color: '#F15450' },

      // Font weights
      bold: { bold: true, characterSpacing: 2 },
      boldText: { bold: true },
      fw600: { font: 'PoppinsSemiBold', bold: true },
      fw900: { bold: true },

      // Reusable margins
      mb20: { marginBottom: 20 },
      mb32: { marginBottom: 32 },

      // Specific Section Styles
      headerSubtitle: {
        fontSize: 14,
        bold: true,
        color: '#F15450',
        characterSpacing: 1.2,
      },

      infoContainerHeader: {
        fontSize: 10,
        bold: true,
        characterSpacing: 2,
        margin: [0, 0, 0, 2],
      },
      infoText: { fontSize: 10, lineHeight: 1.2 },

      tableHeaderCell: {
        font: 'PoppinsSemiBold',
        bold: true,
        fontSize: 10,
        lineHeight: 1.1,
        color: '#fff',
        fillColor: '#F15450',
        alignment: 'center',
        characterSpacing: 2,
      },
      tableBodyText: { fontSize: 9, lineHeight: 1.33 },
      rightAlign: { alignment: 'right' },

      summaryDescription: {
        fontSize: 10,
        alignment: 'right',
        lineHeight: 1,
        textTransform: 'uppercase',
        characterSpacing: 0,
      },
      summaryValue: {
        fontSize: 10,
        alignment: 'right',
        lineHeight: 1,
        characterSpacing: 0,
      },
      invoiceTotalLabel: {
        fontSize: 9,
        bold: true,
        alignment: 'right',
        characterSpacing: 0,
      },
      invoiceTotalValue: {
        fontSize: 9,
        bold: true,
        alignment: 'right',
        characterSpacing: 0,
      },

      descriptionTextParagraph: {
        fontSize: 9,
        lineHeight: 1.33,
        color: '#262B33',
        margin: [0, 16, 0, 0],
      },

      accountDetailsLabel: {
        fontSize: 10,
        bold: true,
        alignment: 'right',
        lineHeight: 1.33,
      },
      accountDetailsValue: { fontSize: 10, lineHeight: 1.33, paddingLeft: 8 },

      uppercase: { textTransform: 'uppercase' },
      invoiceDataStyle: { textTransform: 'uppercase' },
    },

    content: [
      // --- Page 1: Main Invoice ---
      {
        columns: [
          {
            width: '*',
            stack: [
              {
                text: 'INVOICE',
                style: 'headerMainTitle',
                margin: [0, 0, 0, 0],
              },
              { text: '#', style: 'headerSubtitle', margin: [0, 0, 0, 4] },
              {
                table: {
                  widths: [50, 'auto'],
                  body: [
                    [
                      { text: 'DATE:', style: ['f8', 'fw600', 'uppercase'] },
                      {
                        text:
                          invoiceData?.transaction_date?.toUpperCase() ?? '',
                        style: ['f8', 'invoiceDataStyle'],
                        alignment: 'right',
                        margin: [20, 0, 0, 0],
                      },
                    ],
                    [
                      { text: 'DUE TO:', style: ['f8', 'fw600', 'uppercase'] },
                      {
                        text: invoiceData?.due_date?.toUpperCase() ?? '',
                        style: ['f8', 'invoiceDataStyle'],
                        alignment: 'right',
                        margin: [20, 0, 0, 0],
                      },
                    ],
                  ],
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 6],
              },
            ],
          },
          // Right: Logo
          {
            image: logoBase64, // The Base64 string of your logo
            width: 52,
            alignment: 'right', // Aligns the image to the right within its column
            margin: [0, 0, 0, 0], // Adjust margins as needed to position it correctly
          },
        ],
        margin: [0, 6, 0, 6],
      },

      // --- Billed From / Billed To Section ---
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: 'BILLED FROM', style: 'infoContainerHeader' },
              {
                text: invoiceData?.company_info?.CompanyName ?? '',
                style: ['infoText', 'uppercase'],
              },
              {
                text: invoiceData?.company_info?.Street ?? '',
                style: 'infoText',
              },
              {
                text: invoiceData?.company_info?.Location ?? '',
                style: 'infoText',
              },
              ...(invoiceData?.company_info?.GovID
                ? [
                    {
                      text: `Govt. UID VAT ${invoiceData.company_info.GovID}`,
                      style: 'infoText',
                    },
                  ]
                : []),
              {
                text: invoiceData?.company_info?.Phone ?? '',
                style: 'infoText',
              },
              {
                text: invoiceData?.company_info?.Email ?? '',
                style: 'infoText',
              },
            ],
          },
          {
            width: '*',
            alignment: 'right',
            stack: [
              { text: 'BILLED TO', style: 'infoContainerHeader' },
              {
                text: invoiceData.company_name,
                style: ['infoText', 'uppercase'],
              },
              { text: invoiceData.billing_street_1, style: 'infoText' },
              { text: invoiceData.location, style: 'infoText' },
              // { text: `VAT: ${invoiceData.VAT || ''}`, style: 'infoText' },
            ],
          },
        ],
        margin: [0, 10, 0, 32],
      },

      // --- Services Table ---
      {
        table: {
          headerRows: 1,
          widths: [120, '*', 80],
          body: [
            [
              { text: 'SERVICE', style: 'tableHeaderCell', alignment: 'left' },
              {
                text: 'DESCRIPTION',
                style: 'tableHeaderCell',
                alignment: 'left',
              },
              { text: 'AMOUNT', style: 'tableHeaderCell', alignment: 'right' },
            ],
            ...invoiceData?.services?.map((service) => [
              {
                text: service.name,
                style: 'tableBodyText',
                margin: [0, 0, 0, 0],
              },
              {
                text: service.description,
                style: 'tableBodyText',
                margin: [0, 0, 0, 0],
              },
              {
                text: `${invoiceData.currency} ${service.amount}`,
                style: ['tableBodyText', 'rightAlign'],
                margin: [0, 0, 0, 0],
              },
            ]),
          ],
        },
        layout: tableLayoutConfig,
        margin: [0, 0, 0, 0],
      },

      // --- Price Summary Table ---
      {
        columns: [
          { width: '*', text: '' },
          {
            width: 'auto',
            table: {
              widths: [121, 120],
              body: [
                [
                  { text: 'SUBTOTAL', style: ['summaryDescription', 'fw600'] },
                  {
                    text: `${invoiceData.currency} ${invoiceData.amount}`,
                    style: 'summaryValue',
                    margin: [0, 0, 6, 0],
                  },
                ],
                [
                  {
                    text:
                      runtimeBranding?.discountLabel?.toUpperCase() ||
                      'DISCOUNT',
                    style: ['summaryDescription', 'fw600'],
                  },
                  {
                    text: `${invoiceData.currency} ${invoiceData.discount_amount}`,
                    style: 'summaryValue',
                    margin: [0, 0, 6, 0],
                  },
                ],
                ...(invoiceData?.transaction_fee?.length > 0 &&
                invoiceData?.transaction_fee !== '0.00'
                  ? [
                      [
                        {
                          text: 'PAYMENT PROCESSING FEE:',
                          style: ['summaryDescription', 'fw600'],
                        },
                        {
                          text: `${invoiceData.currency} ${invoiceData.transaction_fee}`,
                          style: 'summaryValue',
                          margin: [0, 0, 6, 0],
                        },
                      ],
                    ]
                  : []),
                [
                  { text: 'TAX', style: ['summaryDescription', 'fw600'] },
                  {
                    text: `${invoiceData.currency} ${invoiceData.total_tax_amount}`,
                    alignment: 'right',
                    style: 'summaryValue',
                    margin: [0, 0, 6, 0],
                  },
                ],
                ...(invoiceData?.paid_total?.length > 0 &&
                invoiceData.paid_total !== '0.00'
                  ? [
                      [
                        {
                          text: 'PAID:',
                          style: ['summaryDescription', 'fw600'],
                        },
                        {
                          text: `${invoiceData.currency} ${invoiceData.paid_total}`,
                          style: 'summaryValue',
                          margin: [0, 0, 6, 0],
                        },
                      ],
                    ]
                  : []),
                [
                  {
                    text: 'INVOICE TOTAL',
                    style: ['summaryDescription', 'fw900'],
                  },
                  {
                    text: `${invoiceData.currency} ${invoiceData.total_amount}`,
                    style: ['summaryValue', 'fw600'],
                    margin: [0, 0, 6, 0],
                  },
                ],
                ...(invoiceData?.balance?.length > 0 &&
                invoiceData?.balance !== '0.00'
                  ? [
                      [
                        {
                          text: 'AMOUNT DUE:',
                          style: ['summaryDescription', 'fw900'],
                        },
                        {
                          text: `${invoiceData.currency} ${invoiceData.balance}`,
                          style: ['summaryValue', 'fw600'],
                          margin: [0, 0, 6, 0],
                        },
                      ],
                    ]
                  : []),
              ],
            },
            layout: 'noBorders',
            margin: [0, 20, 0, 0],
          },
        ],
      },

      // Disclaimer Paragraph
      ...(invoiceData?.time_logs?.length > 0
        ? [
            {
              text: 'This invoice includes a detailed breakdown of hours worked. Please refer to the invoice attachment for the time log and calculations.',
              style: 'descriptionTextParagraph',
              margin: [0, 20, 0, 0],
            },
          ]
        : []),

      // --- Page 2: Work Log Summary ---
      ...(invoiceData?.time_logs?.length > 0
        ? [
            {
              pageBreak: 'before',
              columns: [
                {
                  width: '*',
                  stack: [
                    {
                      text: 'WORK LOG SUMMARY',
                      style: ['headerMainTitle', 'f20', 'charSpacing2'],
                      margin: [0, 0, 0, 0],
                    },
                    {
                      text: 'Attachment of Invoice #',
                      style: 'headerSubtitle',
                      margin: [0, 0, 0, 10],
                    },
                    {
                      columns: [
                        {
                          width: 'auto',
                          stack: [
                            { text: 'MONTH:', style: ['f10', 'boldText'] },
                          ],
                        },
                        {
                          width: 'auto',
                          stack: [
                            {
                              text: `${invoiceData.month
                                .toUpperCase()
                                ?.substring(0, 3)}, ${invoiceData.year}`,
                              style: 'f10',
                              alignment: 'right',
                            },
                          ],
                          margin: [20, 0, 0, 0],
                        },
                      ],
                      margin: [0, 40, 0, 0],
                    },
                  ],
                },
                // Right: Logo
                {
                  image: logoBase64, // The Base64 string of your logo
                  width: 52,
                  alignment: 'right', // Aligns the image to the right within its column
                  margin: [0, 0, 0, 0], // Adjust margins as needed to position it correctly
                },
              ],
              margin: [0, 40, 0, 10],
            },
            // Work Log Table
            {
              table: {
                headerRows: 1,
                widths: ['*', 'auto', 'auto', 'auto'],
                body: [
                  [
                    {
                      text: 'NAME',
                      style: 'tableHeaderCell',
                      alignment: 'left',
                    },
                    {
                      text: 'RATE',
                      style: 'tableHeaderCell',
                      alignment: 'left',
                    },
                    {
                      text: 'WORKED TOTAL',
                      style: 'tableHeaderCell',
                      alignment: 'right',
                    },
                    {
                      text: 'TOTAL FEE',
                      style: 'tableHeaderCell',
                      alignment: 'right',
                    },
                  ],
                  ...invoiceData?.time_logs?.map((log) => [
                    {
                      text: log.user_name,
                      style: 'tableBodyText',
                      margin: [0, 0, 0, 0],
                    },
                    ...(log.rate_per_day > 0
                      ? [
                          {
                            text: `${log.rate_per_day_str} ${invoiceData.currency}/day`,
                            style: 'tableBodyText',
                            alignment: 'right',
                            margin: [0, 0, 0, 0],
                          },
                          {
                            text: `${log.total_days} ${
                              log.total_days > 1 ? 'days' : 'day'
                            }`,
                            style: 'tableBodyText',
                            alignment: 'right',
                            margin: [0, 0, 0, 0],
                          },
                        ]
                      : [
                          {
                            text: `${log.rate_per_hour_str} ${invoiceData.currency}/hour`,
                            style: 'tableBodyText',
                            alignment: 'right',
                            margin: [0, 0, 0, 0],
                          },
                          {
                            text: `${log.total_hours} ${
                              log.total_hours > 1 ? 'hours' : 'hour'
                            }`,
                            style: 'tableBodyText',
                            alignment: 'right',
                            margin: [0, 0, 0, 0],
                          },
                        ]),
                    {
                      text: `${invoiceData.currency} ${log.total_fee}`,
                      style: 'tableBodyText',
                      alignment: 'right',
                      margin: [0, 0, 0, 0],
                    },
                  ]),
                ],
              },
              layout: tableLayoutConfig,
              margin: [0, 0, 0, 32],
            },
          ]
        : []),

      // Disclaimer Paragraph
      ...(invoiceData?.adjustment_time_logs?.length > 0
        ? [
            {
              text: `Time logs adjustment for ${invoiceData.adjustment_month} ${invoiceData.adjustment_year}`,
              style: 'descriptionTextParagraph',
              margin: [0, 20, 0, 0],
            },
            // Work Log Table
            {
              table: {
                headerRows: 1,
                widths: ['*', 'auto', 'auto', 'auto'],
                body: [
                  [
                    {
                      text: 'NAME',
                      style: 'tableHeaderCell',
                      alignment: 'left',
                    },
                    {
                      text: 'RATE',
                      style: 'tableHeaderCell',
                      alignment: 'left',
                    },
                    {
                      text: 'WORKED TOTAL',
                      style: 'tableHeaderCell',
                      alignment: 'right',
                    },
                    {
                      text: 'TOTAL FEE',
                      style: 'tableHeaderCell',
                      alignment: 'right',
                    },
                  ],
                  ...invoiceData?.adjustment_time_logs?.map((log) => [
                    {
                      text: log.user_name,
                      style: 'tableBodyText',
                      margin: [0, 0, 0, 0],
                    },
                    {
                      text: `${log.rate_per_hour_str} ${invoiceData.currency}/day`,
                      style: 'tableBodyText',
                      alignment: 'right',
                      margin: [0, 0, 0, 0],
                    },
                    {
                      text: `${log.total_hours} ${
                        log.total_hours > 1 ? 'days' : 'day'
                      }`,
                      style: 'tableBodyText',
                      alignment: 'right',
                      margin: [0, 0, 0, 0],
                    },
                    {
                      text: `${invoiceData.currency} ${log.total_fee}`,
                      style: 'tableBodyText',
                      alignment: 'right',
                      margin: [0, 0, 0, 0],
                    },
                  ]),
                ],
              },
              layout: tableLayoutConfig,
              margin: [0, 0, 0, 32],
            },
          ]
        : []),

      // --- Page 3: Bank Account Details ---
      {
        pageBreak: 'before',
        columns: [
          {
            width: '*',
            stack: [
              {
                text: 'ZAZMIC BANK ACCOUNT DETAILS',
                style: ['headerMainTitle', 'charSpacing1', 'f14'],
                margin: [0, 10, 0, 0],
              },
              {
                text: 'Attachment of Invoice #',
                style: 'headerSubtitle',
                margin: [0, 0, 0, 10],
              },
            ],
          },
          // Right: Logo
          {
            image: logoBase64, // The Base64 string of your logo
            width: 52,
            alignment: 'right', // Aligns the image to the right within its column
            margin: [0, 0, 0, 0], // Adjust margins as needed to position it correctly
          },
        ],
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          widths: [145, '*'], // account-details-label (164px), rest for value
          body: getBankAccountDetails(),
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 0],
      },
    ],
    footer: (currentPage: number, pageCount: number) => ({
      // Pass invoiceData here
      text: `INVOICE #\nPAGE ${currentPage} OF ${pageCount}`, // Dynamic invoice number
      alignment: 'center', // Align the text to the center of the page
      fontSize: 10,
      margin: [0, 0, 0, 0], // Top margin 30, no left/right/bottom margin needed for centering
    }),
  };

  return new Promise<string>((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pdfMake.createPdf(docDefinition as any).getDataUrl((dataUrl: string) => {
      resolve(dataUrl);
    });
  });
};
